import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import {
  Chevron,
  Field,
  ListFooter,
  Option,
  OptionSubtext,
  OptionText,
  OptionsContainer,
  OptionsList,
  SecondaryLabel,
  StatusRow,
  TextInput,
  Wrapper,
} from './Combobox.styles';
import type { ComboboxProps } from './Combobox.types';

export function Combobox({
  options,
  value,
  onValueCommit,
  onDraftChange,
  mode = 'closed',
  size = 'md',
  filterOptions,
  isLoading,
  loadingLabel = 'Loading…',
  optionsErrorLabel,
  noMatchesLabel = 'No matches',
  emptyLabel,
  secondaryLabelPosition = 'inline',
  footer,
  autoHighlight,
  placeholder = 'Select',
  disabled,
  align = 'left',
  'aria-label': ariaLabel,
  hasError,
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [draft, setDraft] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = `${useId()}-listbox`;

  const selectedOption = options.find((o) => o.value === value);
  const shouldFilter = filterOptions ?? mode === 'closed';

  const visibleOptions = useMemo(
    () =>
      shouldFilter && draft
        ? options.filter((o) => o.label.toLowerCase().includes(draft.toLowerCase()))
        : options,
    [options, draft, shouldFilter]
  );

  const releaseDraft = useCallback(() => {
    if (mode === 'open' || draft === null) return;
    setDraft(null);
    onDraftChange?.('');
  }, [mode, draft, onDraftChange]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    releaseDraft();
  }, [releaseDraft]);

  useClickOutside(wrapperRef, handleClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  const nextEnabled = (from: number, delta: 1 | -1) => {
    let i = from === -1 && delta === -1 ? visibleOptions.length - 1 : from + delta;
    while (i >= 0 && i < visibleOptions.length && visibleOptions[i].disabled) i += delta;
    return i >= 0 && i < visibleOptions.length ? i : -1;
  };
  const firstEnabled = () => nextEnabled(-1, 1);
  const lastEnabled = () => nextEnabled(-1, -1);

  const openedByArrow = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      openedByArrow.current = false;
      return;
    }
    if (openedByArrow.current) {
      openedByArrow.current = false;
      return;
    }
    setActiveIndex(autoHighlight ? firstEnabled() : -1);
  }, [isOpen, draft, visibleOptions.length, autoHighlight]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const items = listRef.current?.querySelectorAll('[role="option"]');
    const item = items?.[activeIndex];
    if (item) (item as HTMLElement).scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, activeIndex]);

  const handleSelect = (optionValue: string) => {
    onValueCommit(optionValue);
    setIsOpen(false);
    if (draft !== null) {
      setDraft(null);
      onDraftChange?.('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openedByArrow.current = true;
        setIsOpen(true);
        setActiveIndex(e.key === 'ArrowDown' ? firstEnabled() : lastEnabled());
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((prev) => {
          const next = nextEnabled(prev, delta);
          return next === -1 ? prev : next;
        });
        break;
      }
      case 'Home': {
        e.preventDefault();
        const first = firstEnabled();
        if (first >= 0) setActiveIndex(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = lastEnabled();
        if (last >= 0) setActiveIndex(last);
        break;
      }
      case 'Enter': {
        const option = activeIndex >= 0 ? visibleOptions[activeIndex] : undefined;
        if (option && !option.disabled) {
          e.preventDefault();
          handleSelect(option.value);
          break;
        }
        handleClose();
        break;
      }
    }
  };

  const activeOption = isOpen && activeIndex >= 0 ? visibleOptions[activeIndex] : undefined;
  const optionId = (optionValue: string) => `${listId}-option-${optionValue}`;

  const open = () => {
    if (disabled) return;
    setIsOpen(true);
    inputRef.current?.focus();
  };

  let statusBody: ReactNode;
  if (optionsErrorLabel) statusBody = optionsErrorLabel;
  else if (isLoading) statusBody = loadingLabel;
  else if (visibleOptions.length === 0) statusBody = draft ? noMatchesLabel : emptyLabel;

  const showList = isOpen && (visibleOptions.length > 0 || Boolean(statusBody));

  return (
    <Wrapper ref={wrapperRef} data-open={isOpen}>
      <Field
        $size={size}
        data-open={isOpen}
        data-disabled={disabled || undefined}
        data-error={hasError || undefined}
        onClick={open}
      >
        <TextInput
          ref={inputRef}
          $size={size}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={showList ? listId : undefined}
          aria-activedescendant={activeOption ? optionId(activeOption.value) : undefined}
          aria-label={ariaLabel}
          aria-invalid={hasError || undefined}
          aria-busy={isLoading || undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={draft ?? selectedOption?.label ?? ''}
          onChange={(e) => {
            setDraft(e.target.value);
            onDraftChange?.(e.target.value);
            setIsOpen(true);
          }}
          onFocus={open}
          onBlur={handleClose}
          onKeyDown={handleKeyDown}
        />
        <Chevron
          aria-hidden="true"
          $open={isOpen}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            if (isOpen) handleClose();
            else open();
          }}
        >
          <ChevronDownIcon size={14} />
        </Chevron>
      </Field>
      {showList && (
        <OptionsList
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          data-align={align}
        >
          <OptionsContainer>
            {statusBody ? (
              <StatusRow
                role="status"
                aria-live="polite"
                data-tone={optionsErrorLabel ? 'error' : undefined}
              >
                {statusBody}
              </StatusRow>
            ) : (
              visibleOptions.map((option, index) => (
                <Option
                  key={option.value}
                  id={optionId(option.value)}
                  role="option"
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled}
                  data-selected={option.value === value}
                  data-disabled={option.disabled}
                  data-active={index === activeIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => !option.disabled && handleSelect(option.value)}
                >
                  {option.secondaryLabel && secondaryLabelPosition === 'block' ? (
                    <OptionText>
                      <span>{option.label}</span>
                      <OptionSubtext>{option.secondaryLabel}</OptionSubtext>
                    </OptionText>
                  ) : (
                    <>
                      {option.label}
                      {option.secondaryLabel && (
                        <SecondaryLabel>{option.secondaryLabel}</SecondaryLabel>
                      )}
                    </>
                  )}
                </Option>
              ))
            )}
          </OptionsContainer>
          {footer && <ListFooter>{footer}</ListFooter>}
        </OptionsList>
      )}
    </Wrapper>
  );
}
